import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-daisyui';
import Modal, { ModalProps } from '@/components/modals/Modal/Modal.tsx';
import WhiteBgButton from '@/components/functional/WhiteBgButton/WhiteBgButton.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import { useDnaRefinementContext } from '@/context/DnaRefinementContext.tsx';
import CheckIconBlock from '@/components/ui/CheckIconBlock/CheckIconBlock.tsx';

const DnaChangeSuggestionModal: FC<Omit<ModalProps, 'closeable'>> = ({ onClose, ...modalProps }) => {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const { handleApplyRefinement, handleRejectRefinement, isLoadingAction, currentRefinement } =
    useDnaRefinementContext();

  const handleYes = async () => {
    if (isLoadingAction) return;
    await handleApplyRefinement();
    setConfirmed(true);
  };

  const handleNo = async () => {
    if (isLoadingAction) return;
    await handleRejectRefinement();
    onClose?.();
  };

  return (
    <Modal.Wrapper
      wrapperClassName="w-[400px]"
      {...modalProps}
      onClose={onClose}
      showCloseButton={false}
      closeable={false}
    >
      {!confirmed ? (
        <Modal.Title>
          <h2 className="text-2xl font-feature font-bold text-center">{t('dnaChangeSuggestionModal.title')}</h2>
        </Modal.Title>
      ) : null}

      {!confirmed && !!currentRefinement?.refinement_comment ? (
        <p className="text-text-secondary text-sm italic text-center pb-2">{currentRefinement.refinement_comment}</p>
      ) : null}

      {confirmed ? (
        <div className="flex flex-col gap-4 justify-center items-center pb-4">
          <CheckIconBlock className="w-20 h-20" />
          <p className="font-feature font-bold text-center text-3.5xl italic px-1">
            {t('dnaChangeSuggestionModal.confirmed')}
          </p>
        </div>
      ) : null}

      <Modal.Nav>
        {({ handleClose }) =>
          !confirmed ? (
            <div className="grid grid-cols-2 gap-3 w-full">
              <WhiteBgButton loading={isLoadingAction} className="normal-case" color="primary" onClick={handleNo}>
                {t('button.no')}
              </WhiteBgButton>
              <LoadingButton
                loading={isLoadingAction}
                className="px-10 min-h-[42px] h-[42px]"
                color="primary"
                onClick={handleYes}
              >
                {t('button.yes')}
              </LoadingButton>
            </div>
          ) : (
            <div className="flex w-full justify-center items-center">
              <Button color="primary" className="w-full" onClick={handleClose}>
                {t('button.close')}
              </Button>
            </div>
          )
        }
      </Modal.Nav>
    </Modal.Wrapper>
  );
};

export default DnaChangeSuggestionModal;
